import { useState } from 'react';
import { format } from 'date-fns';

import { Snackbar } from '@atom/mui';
import api from '@atom/utilities/api';
import {
  FILES_ARCHIVE_ENDPOINT,
  FILES_ENDPOINT,
} from '@atom/utilities/endpoints';
import { downloadFile } from '@atom/utilities/fileUtilities';

type DownloadableMedia = {
  name: string;
  fileId: string;
  fileExtension: string;
};

type FileDownloadFunction = (media: DownloadableMedia[]) => Promise<void>;

type ResponseData = {
  loading: boolean;
  error: Error;
};

export const useFileDownload = (): [FileDownloadFunction, ResponseData] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>(null);

  const downloader = async (media: DownloadableMedia[]) => {
    if (!media.length) {
      return;
    }

    if (media.length === 1) {
      const [file] = media;

      downloadFile(
        `${FILES_ENDPOINT}/${file.fileId}`,
        `${file.name}.${file.fileExtension}`,
      );

      return;
    }

    setError(null);
    setLoading(true);

    Snackbar.info({ message: 'Preparing files for download' });

    try {
      const { data: archiveId } = await api.post<string>(
        FILES_ARCHIVE_ENDPOINT,
        media,
      );

      downloadFile(
        `${FILES_ENDPOINT}/${archiveId}`,
        `Files ${format(new Date(), 'yyyy-MM-dd')}.zip`,
      );

      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err);

      Snackbar.error({ message: 'Failed to prepare download' });
    }
  };

  return [downloader, { loading, error }];
};
